import {
  AfterContentChecked,
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  ViewChild,
  OnDestroy, HostListener,
} from '@angular/core';
import * as moment from 'moment';
import filesize from 'filesize/lib/filesize.es6.js';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import remove from 'lodash/remove';
import map from 'lodash/map';
import last from 'lodash/last';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import reverse from 'lodash/reverse';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import {FileFormatService} from "../../services/fileformat.service";
import {ActiveAction, Asset, AssetLibraryItem} from "../../services/asset-library.model";
import {AssetsService} from "../../services/assets.service";
import {RolesService} from "../../../../core/services/roles/roles.service";
import {swalMini} from "../../../../shared/utils/utils";

@Component({
  selector: 'assets-table',
  templateUrl: './assets-table.component.html',
  styleUrls: ['./assets-table.component.scss'],
  providers: [FileFormatService],
})
export class AssetsTableComponent implements OnInit, AfterContentChecked, OnDestroy {
  assetLibraryList: MatTableDataSource<AssetLibraryItem>;
  assets: AssetLibraryItem[];
  defaultTableColumns: string[] = ['name', 'format', 'size', 'resolution', 'uploaded'];
  tableColumnsWithActions: string[] = [...this.defaultTableColumns, 'actions'];
  displayedColumns: string[] = this.defaultTableColumns;
  name = '';
  updateAssetForm: FormGroup;
  activeAction: ActiveAction = {
    id: null,
    action: null,
    item: null,
  };
  confirm: Boolean = false;

  assetsToShow: AssetLibraryItem[] = [];
  assetsPages = {
    pagesLength: 0,
    pages: {},
  };
  currPage: Number = 0;
  endList: Boolean = false;
  loadMore: Boolean = false;
  dataLoaded: Boolean = false;
  loadNext$ = new BehaviorSubject('0');

  @Input() imageOnly: Boolean;
  @Input() mediaFilter: "image" | "document" | "video" = undefined;
  @Input() searchValue: string;
  @Input() canEdit: boolean;
  @Input() canSelect: boolean;
  @Input() assetsStatus: string;
  @Output() checkAssets = new EventEmitter();
  @Output() onSelectAsset = new EventEmitter();
  @Output() getImagesLength = new EventEmitter();

  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private http: HttpClient,
    private fileFormatService: FileFormatService,
    private assetsService: AssetsService,
    private rolesService: RolesService
  ) {
    this.assetLibraryList = new MatTableDataSource();
  }

  ngOnInit() {
    this.checkTableColumns();

    this.getAssets();

    this.updateAssetForm = new FormGroup({
      name: new FormControl('', [Validators.required], this.checkValidName.bind(this)),
    });
  }

  ngAfterContentChecked() {
    this.applyFilter();
    this.checkTableColumns();

    if (this.assetsStatus === 'updated') {
      this.updateAssets();
    }
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.scrollEvent, true);
  }

  scrollEvent = (event: any): void => {
    this.onTableScroll(event);
  }

  updateAssets() {
    this.assetsToShow = [];
    this.assetsPages = {
      pagesLength: 0,
      pages: {},
    };
    this.currPage = 0;
    this.endList = false;
    this.loadMore = false;
    this.getAssets();
  }

  setAssetsPages() {
    let pageNumber = 1;
    const assets = [...this.assets];
    while (assets.length > 20) {
      this.assetsPages.pages[pageNumber] = assets.splice(0, 20);
      pageNumber++;
    }
    this.assetsPages.pages[pageNumber] = assets;
    this.assetsPages.pagesLength = pageNumber;
    this.assetsToShow = this.assetsPages.pages[1];
    this.currPage = 1;
  }

  getData() {
    this.assetLibraryList = new MatTableDataSource(this.assetsToShow);
    this.assetLibraryList.sort = this.sort;
  }

  onTableScroll(e) {
    const tableViewHeight = e.target.offsetHeight;
    const tableScrollHeight = e.target.scrollHeight;
    const scrollLocation = e.target.scrollTop;
    if (tableScrollHeight - scrollLocation - tableViewHeight < 200) {
      if (this.currPage < this.assetsPages.pagesLength && !this.endList) {
        this.loadMore = true;
        window.setTimeout(() => {
          this.loadMore = false;
          // @ts-ignore
          // TODO: integrate GET ASSETS BY PAGES API
          if (this.assetsPages.pages[this.currPage + 1]) {
            // @ts-ignore
            this.currPage++;
            // @ts-ignore
            this.assetsToShow = [...this.assetsToShow, ...this.assetsPages.pages[this.currPage]];
            this.getData();
          }
        }, 2000);
      }
      if (this.assetsPages.pagesLength === this.currPage) {
        this.endList = true;
      }
    }
  }

  getAssets() {

    const contentTypeFilter = (item, type: string) => {
      return item && item.contentType && item.contentType.split('/')[0] === type;
    }

    this.assetsService.getAssetsAPI().subscribe((data: Asset[]) => {
      let assetsList = sortBy(data, item => {
        return item.name.toLowerCase();
      }) ;
      if (this.imageOnly === true) {
        assetsList = filter(data, (item) => {
          return contentTypeFilter(item, "image");
        });
      } else if (this.mediaFilter !== undefined){
        switch (this.mediaFilter) {
          case "image":
            assetsList = filter(data, (item) => {
              return contentTypeFilter(item, "image");
            });
            break
          case "video":
            assetsList = filter(data, (item) => {
              return contentTypeFilter(item, "video");
            });
            break
          case "document":
            assetsList = filter(data, (item) => {
              return item && item.contentType && item.contentType.split('/')[0] === 'application' || item.contentType.split('/')[0] === 'text';
            });
            break
        }
      }
      this.getImageData(assetsList);
      const assets = assetsList && assetsList.length > 0 ? 'hasAssets' : 'noAssets';
      this.checkAssets.emit(assets);
      if (assetsList && assetsList.length) {
        this.getImagesLength.emit(assetsList.length);
      }
    });
  }

  onVisible() {
    if (!this.dataLoaded) {
      this.loadNext$.next('load next chunk');
    }
  }

  checkTableColumns() {
    this.displayedColumns = this.canEdit ? this.tableColumnsWithActions : this.defaultTableColumns;
  }

  getImageData(assetData) {
    this.assets = map(assetData, (item) => {
      const { reference, name, contentType, size, resolution, uploadedDate, mediaURL } = item;
      const newName = this.cutFileName(name);

      return {
        id: reference,
        name: newName.name,
        format: contentType,
        size,
        resolution,
        uploaded: uploadedDate,
        src: mediaURL,
        type: newName.type,
      };
    });

    window.addEventListener('scroll', this.scrollEvent, true);
    this.setAssetsPages();
    this.getData();
  }

  cutFileName(fileName) {
    const nameArr = fileName.split('.');
    const type = last(nameArr);
    nameArr.pop();
    const name = nameArr.join('.');
    return { type, name };
  }

  checkPreviewImg(format: string, src: string): boolean {
    const item = this.fileFormatService.getType(format);
    return !!(src && item.preview && item.type);
  }

  setNoPreviewImg(format: string) {
    const item = this.fileFormatService.getType(format);
    return {
      src: item.type ? '../../../assets/svg/empty.svg' : '../../../assets/svg/invalid.svg',
      type: item.type,
    };
  }

  setFilesize(size: number) {
    return filesize(size);
  }

  setDate(date) {
    return moment(date).format('YYYY / MM / DD');
  }

  onMatSortChange() {
    if (this.sort) {
      const { active, direction } = this.sort;
      this.assets = sortBy(this.assets, item => {
        return (
          active === 'name'
            ? (item.name
                ? item.name.toLowerCase()
                : '')
            : item[active]
        );
      }) ;

      if (direction === 'desc') {
        this.assets = reverse(this.assets);
      }

      this.setAssetsPages();
      this.assetLibraryList.data = this.assetsToShow;
    }
    // this.assetLibraryList.sortingDataAccessor = (item, property) => {
    //   switch (property) {
    //     case 'name':
    //       return item.name ? item.name.toLowerCase() : '';
    //     default:
    //       return item[property];
    //   }
    // };
    //
    // this.assetLibraryList.sort = this.sort;
  }

  checkValidName(control: AbstractControl) {
    const duplicate = find(this.assetLibraryList.data, { name: control.value });
    return new Promise((resolve) => {
      if (duplicate && duplicate.id !== this.activeAction.id) {
        resolve({ nameError: 'Cannot have duplicate file names' });
      }
      if (control.value.trim() === '') {
        resolve({
          nameError: 'File names cannot be just a "space" - needs at least one character',
        });
      }
      if (control.value.includes('/') || control.value.includes('\\')) {
        resolve({ nameError: 'File names cannot contain "\\" or "/"' });
      }
      if (control.value.trim().charAt(0) === '.') {
        resolve({ nameError: 'File names cannot start with "."' });
      }

      resolve(null);
    });
  }

  getValidationError() {
    const name = this.updateAssetForm.get('name');
    if (name && name.errors && name.errors.required) {
      return 'Needs at least one character!';
    }
    if (name && name.errors && name.errors.nameError) {
      return name.errors.nameError;
    }
    return 'Validation Error!';
  }

  applyFilter() {
    if (this.searchValue) {
      this.assetLibraryList.data = this.assets;
    } else {
      this.assetLibraryList.data = this.assetsToShow;
    }
    // tslint:disable-next-line:no-shadowed-variable
    this.assetLibraryList.filterPredicate = (data, filter: string): boolean => {
      return (
        this.checkProperty(data.name).toLowerCase().includes(filter) ||
        this.checkProperty(data.format).toLowerCase().includes(filter) ||
        this.setFilesize(data.size).toString().toLowerCase().includes(filter) ||
        this.checkProperty(data.resolution).toLowerCase().includes(filter) ||
        this.setDate(data.uploaded).toLowerCase().includes(filter)
      );
    };
    this.assetLibraryList.filter = this.searchValue.trim().toLowerCase();
  }

  checkProperty(property) {
    return property ? property : '';
  }

  editImage(id: string, name: string, e: Event) {
    if (this.canEdit) {
      e.stopPropagation();
      this.name = name;
      this.updateAssetForm.setValue({ name });
      this.setActiveAction('edit', id);
    }
  }

  removeImage(id: string, e: Event) {
    if (this.canEdit) {
      e.stopPropagation();
      this.setActiveAction('remove', id);
    }
  }

  previewImage(id: string, e: Event) {
    if (this.canEdit) {
      e.stopPropagation();
      this.setActiveAction('preview', id);
    }
  }

  isActiveAction(action: string, id: string) {
    return this.activeAction.action === action && this.activeAction.id === id;
  }

  setActiveStyles(action: string, id: string) {
    return this.isActiveAction(action, id) ? 'active' : '';
  }

  setActiveAction(action: string, id: string) {
    this.activeAction = {
      action,
      id,
      item: find(this.assetLibraryList.data, { id }),
    };
  }

  editImageInputClick(e: Event) {
    e.stopPropagation();
  }

  updateAssetHandler(id: string, e: Event) {
    e.stopPropagation();
    const currItem = this.activeAction.item;
    const currIndex = findIndex(this.assetLibraryList.data, { id });
    if (currItem && currIndex !== -1) {
      currItem.name = this.name.trim();
      this.assetLibraryList.data[currIndex] = currItem;
    }
    this.assetsService
      .renameFile({
        fileName: `${this.name.trim()}.${currItem.type}`,
        reference: id,
      })
      .subscribe(
        () => {
          this.updateAssetForm.setValue({ name: this.name.trim() });
          swalMini({
            title: 'File renamed successfully',
            type: 'success',
          });
        },
        (err) => {
          swalMini({
            title: err.error.message,
            type: 'error',
          });
        }
      );
    this.clearActiveAction();
  }

  closeAssetEditionHandler(e: Event) {
    e.stopPropagation();
    this.clearActiveAction();
  }

  removeAssetHandler(e: Event) {
    e.stopPropagation();
    const id = this.activeAction.id;
    this.assetsService.deleteAsset(id).subscribe(
      () => {
        remove(this.assetLibraryList.data, { id });
        this.assetLibraryList._updateChangeSubscription();
        swalMini({
          title: 'Removed successfully!',
          type: 'success',
        });
      },
      () => {
        swalMini({
          title: 'Something went wrong!',
          type: 'error',
        });
      }
    );
    this.clearActiveAction();
  }

  searchMark(name: any) {
    if (name) {
      name = name.toString();
      if (this.searchValue) {
        if (this.searchValue !== '') {
          const searchLength = this.searchValue.length;
          const searchIndex = name.toLowerCase().indexOf(this.searchValue.toLowerCase());
          const indexValue = name.substring(searchIndex, searchIndex + searchLength);
          return name
              .toLowerCase()
              .replace(this.searchValue.toLowerCase(), '<mark>' + indexValue + '</mark>');
        }
      }
    }
    return name;
  }

  selectAsset(id: string) {
    if (this.canSelect) {
      if (this.activeAction.id === id) {
        this.clearActiveAction();
      } else {
        this.activeAction = {
          action: 'selected',
          id,
          item: find(this.assetLibraryList.data, { id }),
        };
      }
      this.onSelectAsset.emit(this.activeAction.item);
    }
  }

  @HostListener('click', ['$event'])
  clickDelegation(event) {
    const target = event.target as HTMLElement;
    const tr = target.closest('tr');
    if (!tr) return;

    const classList = target.classList;
    const { id, name } = tr.dataset;

    if (classList.contains('save-btn')) {
      this.updateAssetHandler(id, event);
    } else if (classList.contains('close-btn')) {
      this.closeAssetEditionHandler(event);
    } else if (target.closest('.image-info-block') || classList.contains('edit')) {
      this.editImage(id, name, event);
    } else if (classList.contains('preview')) {
      this.previewImage(id, event);
    } else if (classList.contains('remove')) {
      this.removeImage(id, event);
    }
  }

  showPopup() {
    return this.activeAction.action === 'preview' || this.activeAction.action === 'remove';
  }

  clearActiveAction() {
    this.confirm = false;
    this.activeAction = {
      action: null,
      id: null,
      item: null,
    };
  }

  canEditAssets() {
    return this.rolesService.canEditAssets();
  }

  checkCanRename(id: string) {
    return this.canEditAssets() ? !this.isActiveAction('edit', id) : true;
  }
}
